import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const BestMagicShowOrlandoPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  const faqs = [
    {
      question: "What makes this the best magic show in Orlando?",
      answer: "Mental Hop Scotch combines world-class mentalism with unprecedented audience interaction. Unlike traditional magic shows with big stage illusions viewed from afar, Simon Nomis performs intimate mental magic where you're not just watching—you're participating. The show has earned acclaim for its unique format and Simon's 20+ years of mastery."
    },
    {
      question: "How is this different from theme park magic shows?",
      answer: "Theme park shows focus on large-scale illusions for big crowds. Mental Hop Scotch is an intimate experience where Simon reads minds, makes impossible predictions, and involves the audience directly. Every show is unique because it's based on the thoughts and choices of that night's audience."
    },
    {
      question: "Who is Simon Nomis?",
      answer: "Simon Nomis is a master magician, illusionist, and mentalist with over 20 years of experience. He's known for his charisma, interactive style, and ability to make every audience member feel like a star. In 2026, he presents two shows in Orlando: Mental Hop Scotch (now playing) and Obscuring Reality (coming July)."
    },
    {
      question: "Is there audience participation?",
      answer: "Yes! Audience participation is central to the show. Simon leaps from mind to mind, reading thoughts, influencing choices, and revealing information. Some participants even walk away with cash prizes and props as souvenirs!"
    },
    {
      question: "Where and when can I see the show?",
      answer: "Mental Hop Scotch plays every Saturday at 7:00 PM in Orlando, FL. The show runs 90 minutes with a 15-minute intermission."
    },
    {
      question: "What are the ticket options?",
      answer: "VIP Experience ($70): Front row, meet & greet, t-shirt, photo with Simon, signed memorabilia. Premium Experience ($50): Rows 3-4, poster, priority seating. Standard Experience ($35): Reserved seating, full show experience."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Best Magic Show in Orlando - Mental Hop Scotch",
    "description": "Experience the best magic show in Orlando. Mental Hop Scotch features master mentalist Simon Nomis in an interactive, mind-bending performance.",
    "url": "https://www.simonnomislive.com/best-magic-show-orlando",
    "image": "https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg",
    "performer": { "@type": "Person", "name": "Simon Nomis", "jobTitle": "Master Magician and Mentalist" },
    "location": { "@type": "Place", "name": "Orlando, FL", "address": { "@type": "PostalAddress", "addressLocality": "Orlando", "addressRegion": "FL" } },
    "offers": [{ "@type": "Offer", "name": "VIP Experience", "price": "70", "priceCurrency": "USD" }, { "@type": "Offer", "name": "Standard", "price": "35", "priceCurrency": "USD" }]
  };

  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>Best Magic Show in Orlando | Mental Hop Scotch | Simon Nomis</title>
        <meta name="description" content="Looking for the best magic show in Orlando? Mental Hop Scotch by Simon Nomis is an interactive mentalism experience unlike any other. Book tickets now!" />
        <meta name="keywords" content="best magic show Orlando, Orlando magic show, magic show Orlando FL, top magic shows Orlando, Simon Nomis, mentalist Orlando, illusionist Orlando" />
        <link rel="canonical" href="https://www.simonnomislive.com/best-magic-show-orlando" />
        <meta property="og:title" content="Best Magic Show in Orlando | Mental Hop Scotch" />
        <meta property="og:description" content="Looking for the best magic show in Orlando? Mental Hop Scotch is an interactive mentalism experience!" />
        <meta property="og:url" content="https://www.simonnomislive.com/best-magic-show-orlando" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-black via-purple-900/30 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6"><span className="text-purple-400">Best Magic Show</span> in Orlando</h1>
            <p className="text-xl md:text-2xl text-purple-300 mb-8">Mind-Reading, Predictions & Impossible Feats</p>
            <p className="text-lg text-gray-300 mb-10 leading-relaxed">
              Searching for the <strong className="text-purple-400">best magic show in Orlando</strong>? Look no further than Mental Hop Scotch—a 90-minute mental magic experience that has audiences questioning reality itself. Performed by master mentalist Simon Nomis in Orlando, FL.
            </p>
            <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-10 py-4 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Buy Tickets Now</a>
            <div className="mt-5 flex justify-center gap-1">
              <img alt="Visa" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-visa.png" />
              <img alt="Mastercard" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-mc.png" />
              <img alt="Amex" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-amex.png" />
              <img alt="Discover" width="40" src="https://s3-us-west-2.amazonaws.com/assets.webconnex.com/images/cc-logos/cc-logo-discover.png" />
            </div>
          </div>
        </div>
      </section>

      {/* What Makes It Best */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center">What Makes It <span className="text-purple-400">Orlando's Best</span></h2>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">🧠</div>
                    <div><h3 className="text-xl font-bold text-purple-400 mb-2">Real Mentalism</h3><p className="text-gray-300">Not tricks with props—actual mind reading and psychological illusions that defy explanation.</p></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">🎭</div>
                    <div><h3 className="text-xl font-bold text-purple-400 mb-2">Total Interaction</h3><p className="text-gray-300">The audience IS the show. Your thoughts, choices, and participation drive every performance.</p></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">🏆</div>
                    <div><h3 className="text-xl font-bold text-purple-400 mb-2">20+ Years of Mastery</h3><p className="text-gray-300">Simon Nomis brings two decades of experience to every performance.</p></div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">🎁</div>
                    <div><h3 className="text-xl font-bold text-purple-400 mb-2">Take Home the Magic</h3><p className="text-gray-300">Participants win cash prizes and receive actual props as souvenirs.</p></div>
                  </div>
                </div>
              </div>
              <img src="https://customer-assets.emergentagent.com/job_obscuring-reality/artifacts/nyl6m05g_mental2.jpg" alt="Best Magic Show Orlando" className="rounded-lg shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Tickets */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center"><span className="text-purple-400">Ticket</span> Options</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-b from-yellow-900/30 to-gray-900/30 p-6 rounded-lg border border-yellow-500/30 text-center">
                <h3 className="text-xl font-bold text-yellow-400 mb-2">VIP Experience</h3>
                <div className="text-3xl font-bold text-white mb-4">$70</div>
                <ul className="text-gray-300 text-sm text-left space-y-2"><li>✓ Front row seating</li><li>✓ Meet & greet</li><li>✓ Exclusive t-shirt</li><li>✓ Photo with Simon</li><li>✓ Signed memorabilia</li></ul>
              </div>
              <div className="bg-gradient-to-b from-blue-900/30 to-gray-900/30 p-6 rounded-lg border border-blue-500/30 text-center">
                <h3 className="text-xl font-bold text-blue-400 mb-2">Premium</h3>
                <div className="text-3xl font-bold text-white mb-4">$50</div>
                <ul className="text-gray-300 text-sm text-left space-y-2"><li>✓ Premium seating</li><li>✓ Limited edition poster</li><li>✓ Priority seating</li><li>✓ Program</li></ul>
              </div>
              <div className="bg-gradient-to-b from-green-900/30 to-gray-900/30 p-6 rounded-lg border border-green-500/30 text-center">
                <h3 className="text-xl font-bold text-green-400 mb-2">Standard</h3>
                <div className="text-3xl font-bold text-white mb-4">$35</div>
                <ul className="text-gray-300 text-sm text-left space-y-2"><li>✓ Reserved seating</li><li>✓ Full magic show</li><li>✓ Merchandise access</li><li>✓ Program</li></ul>
              </div>
            </div>
            <div className="text-center mt-8">
              <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-10 py-4 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Get Tickets</a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center"><span className="text-purple-400">Frequently Asked</span> Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg border border-purple-500/30 overflow-hidden">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-purple-900/20 transition-colors">
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <span className="text-purple-400 text-2xl flex-shrink-0">{openFaq === index ? '−' : '+'}</span>
                  </button>
                  {openFaq === index && <div className="px-6 pb-4"><p className="text-gray-300 leading-relaxed">{faq.answer}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-purple-900/30 via-black to-purple-900/30">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Experience <span className="text-purple-400">Orlando's Best Magic</span></h2>
          <a href="https://simonnomismagcentertainment.ticketspice.com/mental-hop-scotch" target="_blank" rel="noreferrer" className="inline-block font-bold text-xl text-white rounded-full px-12 py-5 transition-all duration-300 hover:scale-105" style={{ backgroundColor: 'rgb(181, 36, 189)' }}>Buy Tickets Now</a>
          <p className="text-gray-400 mt-6 text-sm">📍 Orlando, FL | Saturdays at 7PM</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BestMagicShowOrlandoPage;